import { useChatStore } from '@/stores/chat'
import { SHOW_SUPPORT_CHAT } from '@/constants/features'
import { useFavoritesStore } from '@/stores/favorites'
import { useLocationStore } from '@/stores/location'
import { useMenuStore } from '@/stores/menu'
import { useUserStore } from '@/stores/user'
import { getLanguage } from '@/utils/language'
import { FLOW, track } from '@/utils/track'

/**
 * Стартовая загрузка приложения — аналог `getDataCommonData` из лейаута
 * веб-версии. Меню нужно везде: корзина, избранное и шит товара берут
 * данные из `productsEnum`, поэтому тянем его до открытия любой страницы,
 * а не на входе в «Меню».
 *
 * Регионы и филиалы идут тем же заходом: без них не открыть карту
 * и не показать список для самовывоза. Профиль — тоже: с него берутся имя,
 * телефон и баллы на оформлении заказа, а не только на странице профиля.
 */
export function useAppBootstrap() {
  const menuStore = useMenuStore()
  const locationStore = useLocationStore()
  const userStore = useUserStore()
  const favoritesStore = useFavoritesStore()
  const chatStore = useChatStore()

  let started = false

  /**
   * Персональное меню могло не прийти: точка ушла из зоны или её филиал ещё
   * закрыт. Показывать пустой каталог нельзя, поэтому подставляем витрину
   * региона — она от координат не зависит. Только если показывать нечего:
   * уже загруженное меню затирать общими ценами не за чем.
   */
  async function loadMenu(): Promise<void> {
    await menuStore.getMenu()
    if (menuStore.menuError && !menuStore.products.length) await menuStore.getRegionMenu()
  }

  async function bootstrap(): Promise<void> {
    if (started) return
    started = true

    /* Открытие считаем здесь: сюда приходят ровно один раз за запуск */
    track(FLOW.appOpened, { authorized: userStore.isAuthorized, language: getLanguage() })

    /* Избранное нужно сразу: сердце на карточке рисуется на первом же экране */
    await Promise.all([
      loadMenu(),
      locationStore.loadDictionaries(),
      userStore.isAuthorized ? userStore.getUserById(userStore.userId) : Promise.resolve(),
      favoritesStore.load(),
    ])

    /*
     * Поддержка — фоном и не в общем ожидании: экран её не ждёт, а ответ
     * оператора должен догонять клиента на любой странице, а не только
     * когда он сам заглянет в чат.
     */
    if (SHOW_SUPPORT_CHAT && userStore.isAuthorized) void chatStore.watchInbox()
  }

  return { bootstrap }
}
